import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Copy,
  FileText,
  Film,
  ImageIcon,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatMediaBytes,
  whatsappMediaApi,
  type WhatsAppMediaCategory,
  type WhatsAppMediaItem,
} from '@/api/whatsappMedia';

const CATEGORY_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All media' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'document', label: 'Documents' },
];

function MediaThumbnail({ item }: { item: WhatsAppMediaItem }) {
  if (item.mediaCategory === 'image') {
    return (
      <img
        src={item.publicUrl}
        alt={item.originalName}
        className="h-12 w-12 rounded object-cover border bg-muted"
      />
    );
  }
  if (item.mediaCategory === 'video') {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded border bg-muted">
        <Film className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded border bg-muted">
      <FileText className="h-5 w-5 text-muted-foreground" />
    </div>
  );
}

export default function WhatsAppMediaLibraryPage() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [previewItem, setPreviewItem] = useState<WhatsAppMediaItem | null>(null);

  const statsQuery = useQuery({
    queryKey: ['wa-media-stats'],
    queryFn: async () => (await whatsappMediaApi.stats()).data.data,
  });

  const listQuery = useQuery({
    queryKey: ['wa-media-library', categoryFilter, search],
    queryFn: async () => {
      const res = await whatsappMediaApi.list({
        category:
          categoryFilter === 'all' ? undefined : (categoryFilter as WhatsAppMediaCategory),
        search: search.trim() || undefined,
      });
      return res.data.data.items;
    },
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => whatsappMediaApi.upload(file),
    onSuccess: () => {
      toast.success('Media uploaded');
      qc.invalidateQueries({ queryKey: ['wa-media-library'] });
      qc.invalidateQueries({ queryKey: ['wa-media-stats'] });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (e: any) => {
      if (e?.response?.status === 413) {
        toast.error(
          'Upload blocked by server size limit (HTTP 413). Use Import from URL below, or set nginx client_max_body_size 64m on EC2.'
        );
        return;
      }
      toast.error(e?.response?.data?.message || 'Upload failed');
    },
  });

  const importUrlMut = useMutation({
    mutationFn: (url: string) => whatsappMediaApi.uploadFromUrl(url),
    onSuccess: () => {
      toast.success('Media imported from URL');
      setImportUrl('');
      qc.invalidateQueries({ queryKey: ['wa-media-library'] });
      qc.invalidateQueries({ queryKey: ['wa-media-stats'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Import failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => whatsappMediaApi.remove(id),
    onSuccess: () => {
      toast.success('Media deleted');
      setPreviewItem(null);
      qc.invalidateQueries({ queryKey: ['wa-media-library'] });
      qc.invalidateQueries({ queryKey: ['wa-media-stats'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const items = listQuery.data ?? [];
  const stats = statsQuery.data;

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            WhatsApp Media Library
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload images, videos, and PDFs once. THMS generates public HTTPS URLs for WhatsApp
            Cloud API campaign headers.
          </p>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Total files</div>
                <div className="text-xl font-semibold">{stats.totalFiles}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Storage used</div>
                <div className="text-xl font-semibold">{formatMediaBytes(stats.storageUsedBytes)}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Campaign sends</div>
                <div className="text-xl font-semibold">{stats.totalUsageCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Campaigns using media</div>
                <div className="text-xl font-semibold">{stats.totalCampaignCount}</div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Upload media</Label>
              <div className="flex gap-2 mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.mp4,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadMut.mutate(file);
                  }}
                />
                <Button
                  type="button"
                  disabled={uploadMut.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Choose file
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Images (jpg, png, webp) max 10 MB · Videos (mp4) max 50 MB · PDF max 20 MB
              </p>
            </div>

            <div className="flex-1 min-w-[280px]">
              <Label>Import from URL (bypasses nginx 413)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="https://cdn.example.com/video.mp4"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={importUrlMut.isPending || !importUrl.trim()}
                  onClick={() => importUrlMut.mutate(importUrl.trim())}
                >
                  {importUrlMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Import'
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Direct HTTPS link to the file (S3/CloudFront). Google Drive preview links do not work.
              </p>
            </div>

            <div>
              <Label>Filter</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_FILTERS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label>Search</Label>
              <Input
                className="mt-1"
                placeholder="Search by file name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Media files</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Preview</TableHead>
                  <TableHead>File name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No media files yet
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={() => setPreviewItem(item)}
                          title="Preview"
                        >
                          <MediaThumbnail item={item} />
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{item.originalName}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-xs">
                          {item.publicUrl}
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{item.mediaCategory}</TableCell>
                      <TableCell>{formatMediaBytes(item.fileSize)}</TableCell>
                      <TableCell>
                        {format(new Date(item.createdAt), 'dd MMM yyyy')}
                        {item.uploadedByName && (
                          <div className="text-xs text-muted-foreground">{item.uploadedByName}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{item.usageCount} sends</div>
                        <div className="text-xs text-muted-foreground">
                          {item.campaignCount} campaign{item.campaignCount === 1 ? '' : 's'}
                        </div>
                        {item.lastUsedAt && (
                          <div className="text-xs text-muted-foreground">
                            Last used {format(new Date(item.lastUsedAt), 'dd MMM yyyy')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => copyUrl(item.publicUrl)}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1" />
                            Copy URL
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            disabled={deleteMut.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete ${item.originalName}?`)) {
                                deleteMut.mutate(item.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {previewItem && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{previewItem.originalName}</CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewItem(null)}>
              Close
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {previewItem.mediaCategory === 'image' && (
              <img
                src={previewItem.publicUrl}
                alt={previewItem.originalName}
                className="max-h-96 rounded border"
              />
            )}
            {previewItem.mediaCategory === 'video' && (
              <video
                src={previewItem.publicUrl}
                controls
                className="max-h-96 max-w-full rounded border"
              />
            )}
            {previewItem.mediaCategory === 'document' && (
              <iframe
                src={previewItem.publicUrl}
                title={previewItem.originalName}
                className="h-96 w-full rounded border"
              />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => copyUrl(previewItem.publicUrl)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy URL
              </Button>
              <Button type="button" variant="outline" asChild>
                <a href={previewItem.publicUrl} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
